import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Chip,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Close } from "@mui/icons-material";
import { useAttachmentRemovals } from "../../hooks/useAttachmentRemovals";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";

import axios from "../../api/axios";
import { useCRUD } from "../../api/crud";
import { useAuthContext, useIsServiceRole } from "../../contexts/auth/auth.hooks";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import FileDropZoneField from "../ui/fileDropZoneField/fileDropZoneField.component";
import CustomDatePicker from "../ui/customDatePicker/customDatePicker.component";
import EquipmentAutocomplete from "../ui/equipmentAutocomplete/equipmentAutocomplete.component";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import {
  CLIENTS_ENDPOINT,
  CONTRACT_TERMS_ENDPOINT,
  EQUIPMENTS_ENDPOINT,
  JOBS_ENDPOINT,
  JOB_ISSUES_ENDPOINT,
  JOB_KIND_OPTIONS,
  JOB_SERVICES_ENDPOINT,
  JOB_STATUS_OPTIONS,
  NUMERATION_SCHEMA_ENDPOINT,
  TENANT_SETTINGS_ENDPOINT,
  USERS_ENDPOINT,
  ATTACHMENTS_ENDPOINT,
  ERROR_SNACKBAR,
  SUCCESS_SNACKBAR,
} from "../../consts";
import styles from "./jobForm.module.css";
import { formatAddress, highlightMatch } from "../../utils";
import { AttachmentOrigin, Job, JobTask } from "../../types/jobs";
import { Equipment } from "../../types/equipments";

// ============================================================================
// Form Types & Constants
// ============================================================================

type AttachmentChip = {
  id: number;
  label: string;
  url?: string;
  origin?: AttachmentOrigin;
  // True once the file is bound to a task server-side. Removing one of those
  // is only carried out when the form is saved - see useAttachmentRemovals.
  persisted?: boolean;
};

type JobTaskRow = {
  id?: number;
  equipment: string | number | null;
  issues: OptionRecord[];
  services: OptionRecord[];
  description: string;
  // Files only ever describe the machine they were taken of, so they belong
  // to the task rather than to the job as a whole.
  attachments: AttachmentChip[];
};

type JobFormValues = {
  kind: string;
  status: string;
  numeration_schema_id: OptionRecord | null;
  number: string;
  planned_for: string | null;
  assigned_to: OptionRecord[];
  client_id: string | number | null;
  complaint_for_id: string | number | null;
  description: string;
  tasks: JobTaskRow[];
};

// Utility Functions
const createDefaultTaskRow = (): JobTaskRow => ({
  equipment: null,
  issues: [],
  services: [],
  description: "",
  attachments: [],
});

const attachmentChipFrom = (
  row: { id: number; attachment?: string; origin?: AttachmentOrigin },
  fallbackLabel: string,
  persisted: boolean
): AttachmentChip => {
  const url = row.attachment || "";
  return {
    id: row.id,
    label: url ? url.replace(/^.*\//, "") : fallbackLabel,
    url,
    origin: row.origin ?? null,
    persisted,
  };
};

const buildDefaultFormValues = (defaultKind = "EWIC"): JobFormValues => ({
  kind: defaultKind,
  status: "",
  numeration_schema_id: null,
  number: "",
  planned_for: dayjs().add(1, "day").format("YYYY-MM-DD"),
  assigned_to: [],
  client_id: null,
  complaint_for_id: null,
  description: "",
  tasks: [createDefaultTaskRow()],
});

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    if (typeof first === "object" && first && "message" in first) {
      return (first as { message?: string }).message ?? null;
    }
  }
  if (typeof value === "object" && value && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

// ============================================================================
// Main Component
// ============================================================================

interface JobFormProps {
  jobId?: string | number;
  onSuccess?: (createdJob?: Job) => void;
  initialClientId?: string | number | null;
  initialEquipmentId?: string | number | null;
  initialComplaintForId?: string | number | null;
  // Restricts the rendered form to the Tasks section only (used by the
  // "Update tasks" action available to users without full job-edit rights).
  // The rest of the job's fields are still fetched, kept in state and
  // resubmitted unchanged, so nothing else on the job is affected.
  onlyTasks?: boolean;
}

export default function JobForm({
  jobId,
  onSuccess,
  initialClientId,
  initialEquipmentId,
  initialComplaintForId,
  onlyTasks = false,
}: JobFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  // A technician may add their own files but not take away the ones the job
  // was reported with - those are somebody else's account of the fault.
  const isServiceRole = useIsServiceRole();
  const { create, update, get, loading } = useCRUD(JOBS_ENDPOINT);
  const attachmentRemovals = useAttachmentRemovals();
  const isEditMode = Boolean(jobId);
  const statusDefault = JOB_STATUS_OPTIONS[0]?.value || "";
  const canAddAnyJob = Boolean(currentUser?.permissions?.includes("service.add_job"));
  const canAddInternalJobOnly = Boolean(
    currentUser?.permissions?.includes("service.add_internal_job") && !canAddAnyJob
  );
  const defaultJobKind = !isEditMode && canAddInternalJobOnly ? "I" : "EWIC";

  // =========================================================================
  // State Management
  // =========================================================================

  const [formData, setFormData] = useState<JobFormValues>(() =>
    buildDefaultFormValues(defaultJobKind)
  );
  const [initializing, setInitializing] = useState(true);
  const [serverErrors, setServerErrors] = useState<any>(null);
  const [numberLoading, setNumberLoading] = useState(false);
  // Index of the task row whose drop zone is mid-upload, so only that row's
  // zone is disabled while a file is on its way.
  const [uploadingTaskIndex, setUploadingTaskIndex] = useState<number | null>(null);
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [selectedClientOption, setSelectedClientOption] = useState<OptionRecord | null>(null);

  // Consolidated autocomplete state
  const [autocompleteData, setAutocompleteData] = useState<{
    options: Record<string, OptionRecord[]>;
    loading: Record<string, boolean>;
  }>({
    options: {},
    loading: {},
  });

  // Refs
  const formDataRef = useRef(formData);
  const previousClientIdRef = useRef<string | number | null>(formData.client_id);
  const pendingClientLookupIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // =========================================================================
  // Initialization Effect
  // =========================================================================

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        if (jobId) {
          const jobResponse = await get(jobId);
          if (cancelled) return;
          if (jobResponse?.data) {
            hydrateFormFromJob(jobResponse.data);
          }
        } else {
          const initialUpdates: Partial<JobFormValues> = {
            status: statusDefault,
          };

          if (initialClientId != null) {
            initialUpdates.client_id = initialClientId;
            previousClientIdRef.current = initialClientId;
            // Fetch client display name
            try {
              const { data } = await axios.get(`${CLIENTS_ENDPOINT}${initialClientId}/`);
              if (!cancelled && data) setSelectedClientOption(data);
            } catch {
              // client might not be found, silent fail
            }
          }

          if (initialEquipmentId != null) {
            initialUpdates.tasks = [{ ...createDefaultTaskRow(), equipment: initialEquipmentId }];
            // Fetch equipment for display in autocomplete
            try {
              const { data } = await axios.get(`${EQUIPMENTS_ENDPOINT}${initialEquipmentId}/`);
              if (!cancelled && data) {
                setAutocompleteData((prev) => ({
                  ...prev,
                  options: { ...prev.options, "tasks.equipment": [data] },
                }));
              }
            } catch {
              // equipment might not be found, silent fail
            }
          }

          let complaintPrefilled = false;
          if (initialComplaintForId != null) {
            initialUpdates.complaint_for_id = initialComplaintForId;
            try {
              const { data } = await axios.get(`${JOBS_ENDPOINT}${initialComplaintForId}/`);
              if (!cancelled && data) {
                applyComplaintTargetPrefill(data);
                complaintPrefilled = true;
              }
            } catch {
              // source job might not be found, silent fail
            }
          }

          if (!complaintPrefilled) {
            // Prefill the numeration schema configured as the workspace default for jobs
            try {
              const { data: tenantSettings } = await axios.get(`${TENANT_SETTINGS_ENDPOINT}settings/`);
              const defaultSchemaId = tenantSettings?.default_job_numeration_schema;
              if (!cancelled && defaultSchemaId) {
                const { data: schema } = await axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${defaultSchemaId}/`);
                if (!cancelled && schema) {
                  initialUpdates.numeration_schema_id = schema;
                  setAutocompleteData((prev) => ({
                    ...prev,
                    options: { ...prev.options, numeration_schema_id: [schema] },
                  }));
                }
              }
            } catch {
              // tenant settings or default schema might not be found, silent fail
            }
          }

          if (!cancelled && !complaintPrefilled) {
            setFormData((prev) => ({ ...prev, ...initialUpdates }));
            if (initialUpdates.numeration_schema_id) {
              fetchNextNumber(initialUpdates.numeration_schema_id.id);
            }
          }
        }
      } catch {
        setFormData((prev) => ({
          ...prev,
          status: prev.status || statusDefault,
        }));
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // =========================================================================
  // Client Change Effect
  // =========================================================================

  const clientId = formData.client_id;
  useEffect(() => {
    if (initializing || previousClientIdRef.current === clientId) return;
    previousClientIdRef.current = clientId;

    // Clear equipment when client changes
    setFormData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((row) => ({ ...row, equipment: null })),
    }));
    setAutocompleteData((prev) => ({
      ...prev,
      options: { ...prev.options, "tasks.equipment": [] },
    }));
  }, [clientId, initializing]);

  // =========================================================================
  // Helper Functions
  // =========================================================================

  function applyComplaintTargetPrefill(sourceJob: Job) {
    const rawTasks = sourceJob.tasks ?? sourceJob.services ?? [];
    const tasks: JobTaskRow[] =
      Array.isArray(rawTasks) && rawTasks.length > 0
        ? rawTasks.map((task: JobTask) => ({
            equipment: task.equipment?.id ?? null,
            issues: (task.issues ?? []).map((issue) =>
              typeof issue === "object" ? issue : ({ id: issue } as OptionRecord)
            ),
            services: (task.services ?? []).map((service) =>
              typeof service === "object" ? service : ({ id: service } as OptionRecord)
            ),
            description: task.description ?? "",
            // Deliberately not copied: the files on the job being complained
            // about document that visit, not this new report.
            attachments: [],
          }))
        : [createDefaultTaskRow()];

    const hydratedClientId = sourceJob.client?.id ?? sourceJob.client_id ?? null;
    const numerationSchema = sourceJob.numeration_schema ?? null;
    previousClientIdRef.current = hydratedClientId;
    setSelectedClientOption(sourceJob.client ?? null);

    setFormData((prev) => ({
      ...prev,
      kind: "C",
      status: statusDefault,
      client_id: hydratedClientId,
      complaint_for_id: initialComplaintForId ?? sourceJob.id,
      numeration_schema_id: numerationSchema,
      tasks,
    }));

    if (numerationSchema?.id) {
      fetchNextNumber(numerationSchema.id);
    }

    const equipmentOptions: OptionRecord[] = [];
    const equipmentIds = new Set<string | number>();
    rawTasks.forEach((task: JobTask) => {
      const equipment = task.equipment;
      if (equipment?.id != null && !equipmentIds.has(equipment.id)) {
        equipmentIds.add(equipment.id);
        equipmentOptions.push(equipment);
      }
    });

    const allIssues: OptionRecord[] = [];
    const issueIds = new Set<string | number>();
    rawTasks.forEach((task: JobTask) => {
      task.issues?.forEach((issue) => {
        const issueId = typeof issue === "object" ? issue.id : issue;
        if (issueId != null && !issueIds.has(issueId)) {
          issueIds.add(issueId);
          if (typeof issue === "object" && issue.id) allIssues.push(issue);
        }
      });
    });

    const allTaskServices: OptionRecord[] = [];
    const serviceIds = new Set<string | number>();
    rawTasks.forEach((task: JobTask) => {
      task.services?.forEach((service) => {
        const serviceId = typeof service === "object" ? service.id : service;
        if (serviceId != null && !serviceIds.has(serviceId)) {
          serviceIds.add(serviceId);
          if (typeof service === "object" && service.id) allTaskServices.push(service);
        }
      });
    });

    setAutocompleteData((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        ...(numerationSchema ? { numeration_schema_id: [numerationSchema] } : {}),
        ...(equipmentOptions.length > 0 ? { "tasks.equipment": equipmentOptions } : {}),
        ...(allIssues.length > 0 ? { "tasks.issues": allIssues } : {}),
        ...(allTaskServices.length > 0 ? { "tasks.services": allTaskServices } : {}),
      },
    }));
  }

  function hydrateFormFromJob(job: Job) {
    const rawTasks = job.tasks ?? job.services ?? [];
    const tasks: JobTaskRow[] =
      Array.isArray(rawTasks) && rawTasks.length > 0
        ? rawTasks.map((task: any) => ({
            id: task.id,
            equipment: task.equipment?.id ?? task.equipment ?? null,
            issues: task.issues,
            services: task.services,
            description: task.description ?? "",
            attachments: Array.isArray(task.attachments)
              ? task.attachments.map((attachment: { id: number; attachment?: string }) =>
                  attachmentChipFrom(attachment, t("Attachment"), true)
                )
              : [],
          }))
        : [createDefaultTaskRow()];

    const hydratedClientId = job.client?.id ?? job.client_id ?? null;
    previousClientIdRef.current = hydratedClientId;
    setSelectedClientOption(job.client ?? null);

    setFormData({
      kind: job.kind ?? "",
      status: job.status ?? statusDefault,
      numeration_schema_id: job.numeration_schema ?? null,
      number: job.number ?? "",
      planned_for: job.planned_for ?? null,
      assigned_to: job.assigned_to_details.map((detail) => ({
        id: detail.id,
        full_name: `${detail.first_name} ${detail.last_name} (${detail.email})`,
      })),
      client_id: hydratedClientId,
      complaint_for_id: job.complaint_for_id ?? null,
      description: job.description ?? "",
      tasks,
    });

    // Populate initial options from job data
    const newOptions: Record<string, OptionRecord[]> = {};
    if (job.numeration_schema) newOptions.numeration_schema_id = [job.numeration_schema];
    if (Array.isArray(job.assigned_to_details) && job.assigned_to_details.length > 0) {
      newOptions.assigned_to = job.assigned_to_details;
    }

    const allIssues: OptionRecord[] = [];
    const issueIds = new Set<string | number>();
    rawTasks.forEach((task: any) => {
      task.issues?.forEach((issue: any) => {
        const id = typeof issue === "object" ? issue.id : issue;
        if (id != null && !issueIds.has(id)) {
          issueIds.add(id);
          if (typeof issue === "object" && issue.id) allIssues.push(issue);
        }
      });
    });
    if (allIssues.length > 0) newOptions["tasks.issues"] = allIssues;

    const allTaskServices: OptionRecord[] = [];
    const serviceIds = new Set<string | number>();
    rawTasks.forEach((task: any) => {
      task.services?.forEach((svc: any) => {
        const id = typeof svc === "object" ? svc.id : svc;
        if (id != null && !serviceIds.has(id)) {
          serviceIds.add(id);
          if (typeof svc === "object" && svc.id) allTaskServices.push(svc);
        }
      });
    });
    if (allTaskServices.length > 0) newOptions["tasks.services"] = allTaskServices;

    const allEquipment: OptionRecord[] = [];
    const equipmentIds = new Set<string | number>();
    rawTasks.forEach((task: any) => {
      if (task.equipment && typeof task.equipment === "object" && task.equipment.id) {
        if (!equipmentIds.has(task.equipment.id)) {
          equipmentIds.add(task.equipment.id);
          allEquipment.push(task.equipment);
        }
      }
    });
    if (allEquipment.length > 0) newOptions["tasks.equipment"] = allEquipment;

    setAutocompleteData((prev) => ({
      ...prev,
      options: { ...prev.options, ...newOptions },
    }));
  }

  function buildClientValue(): OptionRecord | null {
    if (formData.client_id === null || formData.client_id === undefined || formData.client_id === "") {
      return null;
    }

    if (
      selectedClientOption?.id !== undefined &&
      String(selectedClientOption.id) === String(formData.client_id)
    ) {
      return selectedClientOption;
    }

    return { id: formData.client_id, name: String(formData.client_id) } as OptionRecord;
  }

  function getFieldError(field: keyof JobFormValues): string | null {
    return extractErrorMessage(serverErrors?.[field as string]);
  }

  function getServiceFieldError(rowIndex: number, field: keyof JobTaskRow): string | null {
    if (!Array.isArray(serverErrors?.tasks)) return null;
    const rowErrors = serverErrors.tasks[rowIndex];
    return rowErrors ? extractErrorMessage(rowErrors[field as string]) : null;
  }

  async function fetchNextNumber(schemaId: string | number) {
    setNumberLoading(true);
    try {
      const { data } = await axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${schemaId}/next/`);
      const nextValue = data?.value ?? data?.result ?? data?.number ?? data;
      setFormData((prev) => ({
        ...prev,
        number: nextValue ? String(nextValue) : "",
      }));
    } catch (err) {
      console.error("Failed to fetch next job number", err);
      enqueueSnackbar(t("Unable to fetch next number."), ERROR_SNACKBAR);
    } finally {
      setNumberLoading(false);
    }
  }

  function handleSubmit() {
    setServerErrors(null);
    const payload = {
      kind: formData.kind || undefined,
      status: formData.status || undefined,
      numeration_schema_id: formData.numeration_schema_id?.id || undefined,
      number: formData.number || undefined,
      planned_for: formData.planned_for || undefined,
      assigned_to: formData.assigned_to.map(i => i.id),
      client_id: formData.client_id || undefined,
      complaint_for_id: !isEditMode && formData.complaint_for_id ? formData.complaint_for_id : undefined,
      description: formData.description || undefined,
      tasks: formData.tasks.map((task) => ({
        id: task.id,
        equipment: task.equipment || undefined,
        issues: task.issues.map(i => i.id),
        services: task.services.map(i => i.id),
        description: task.description || undefined,
        attachment_ids:
          task.attachments.length > 0 ? task.attachments.map((c) => c.id) : undefined,
      })),
    };

    const request = isEditMode
      ? update({ id: jobId, ...payload })
      : create(payload);

    request.then(async (response) => {
      if (response?.success) {
        // Only now are the files the user took off the form actually deleted:
        // up to this point closing the form would have left them untouched.
        const removalsApplied = await attachmentRemovals.commit();
        if (!removalsApplied) {
          enqueueSnackbar(t("Failed to remove attachment."), ERROR_SNACKBAR);
        }

        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        if (!isEditMode) {
          setFormData((prev) => ({
            ...buildDefaultFormValues(defaultJobKind),
            status: statusDefault || prev.status,
          }));
          setSelectedClientOption(null);
          setAutocompleteData({ options: {}, loading: {} });
        }
        setServerErrors(null);
        onSuccess?.(isEditMode ? undefined : (response?.data as Job | undefined));
      } else {
        const errorData = response?.errorData;
        setServerErrors(errorData);
        const failureMessage =
          extractErrorMessage(errorData?.message) ||
          extractErrorMessage(errorData?.detail) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
      }
    });
  }

  function handleTaskFieldChange(
    index: number,
    field: keyof JobTaskRow,
    value: OptionRecord[] | Equipment | string | number | null | (string | number)[]
  ) {
    setFormData((prev) => {
      const tasks = [...prev.tasks];
      tasks[index] = { ...tasks[index], [field]: value } as JobTaskRow;
      return { ...prev, tasks };
    });
  }

  function addTaskRow() {
    setFormData((prev) => ({
      ...prev,
      tasks: [...prev.tasks, createDefaultTaskRow()],
    }));
  }

  function removeTaskRow(index: number) {
    setFormData((prev) => {
      if (prev.tasks.length === 1) {
        return { ...prev, tasks: [createDefaultTaskRow()] };
      }
      return {
        ...prev,
        tasks: prev.tasks.filter((_, i) => i !== index),
      };
    });
  }

  // The file is uploaded the moment it is picked, which leaves an attachment
  // owned by nothing; saving the job is what binds it to the task it was
  // dropped on. Rows are addressed by index because a task has no id until it
  // is saved.
  async function handleTaskAttachmentFile(index: number, file: File) {
    if (!file) return;
    setUploadingTaskIndex(index);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("attachment", file);
      const { data } = await axios.post(ATTACHMENTS_ENDPOINT, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.id != null) {
        const chip = attachmentChipFrom(data, file.name, false);
        setFormData((prev) => {
          const tasks = [...prev.tasks];
          tasks[index] = {
            ...tasks[index],
            attachments: [...tasks[index].attachments, chip],
          };
          return { ...prev, tasks };
        });
      }
    } catch (err) {
      console.error("Attachment upload failed", err);
      enqueueSnackbar(t("Failed to upload file."), ERROR_SNACKBAR);
    } finally {
      setUploadingTaskIndex(null);
    }
  }

  // A file that is already on the task is only taken off the list here; it is
  // deleted once the form is saved, so closing the form instead puts it back.
  async function handleRemoveTaskAttachment(index: number, chip: AttachmentChip) {
    const removed = await attachmentRemovals.remove(chip.id, Boolean(chip.persisted));
    if (!removed) {
      enqueueSnackbar(t("Failed to remove attachment."), ERROR_SNACKBAR);
      return;
    }

    setFormData((prev) => {
      const tasks = [...prev.tasks];
      tasks[index] = {
        ...tasks[index],
        attachments: tasks[index].attachments.filter((c) => c.id !== chip.id),
      };
      return { ...prev, tasks };
    });
  }

  // =========================================================================
  // Layout & Styling
  // =========================================================================
  // Styles are defined in jobForm.module.css and imported as CSS module

  // =========================================================================
  // Memoized Values
  // =========================================================================

  const generalError = useMemo(() => {
    if (!serverErrors) return null;
    return (
      extractErrorMessage(serverErrors?.message) ||
      extractErrorMessage(serverErrors?.detail) ||
      extractErrorMessage(serverErrors?.non_field_errors)
    );
  }, [serverErrors]);

  const availableJobKindOptions = useMemo(() => {
    if (isEditMode || !canAddInternalJobOnly) {
      return JOB_KIND_OPTIONS;
    }

    return JOB_KIND_OPTIONS.filter((option) => option.value === "I");
  }, [canAddInternalJobOnly, isEditMode]);

  const isNumberDisabled = Boolean(formData.numeration_schema_id);

  useEffect(() => {
    if (isEditMode || !canAddInternalJobOnly) {
      return;
    }

    setFormData((prev) => {
      if (prev.kind === "I" && prev.complaint_for_id == null) {
        return prev;
      }

      return {
        ...prev,
        kind: "I",
        complaint_for_id: null,
      };
    });
  }, [canAddInternalJobOnly, isEditMode]);

  if (initializing) {
    return <CustomCircularProgress />;
  }

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <Box className={styles.formContainer}>
      {generalError && (
        <Typography variant="body2" className={styles.errorText}>
          {generalError}
        </Typography>
      )}

      {/* Client */}
      {!onlyTasks && (
      <Box>
        <CustomAutocomplete
          size="small"
          apiConfig={{
            url: `${CLIENTS_ENDPOINT}for-job-form/`,
          }}
          value={buildClientValue()}
          onInputChange={(_, input, reason) => {
            if (reason === "input") {
              setClientSearchInput(input);
            }
          }}
          onChange={(_, newValue) => {
            setSelectedClientOption(newValue ?? null);
            setFormData((prev) => ({ ...prev, client_id: newValue ? newValue.id : null }));
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Client")}
              error={Boolean(getFieldError("client_id"))}
              helperText={getFieldError("client_id") || ""}
              className={styles.inputBase}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            const address = formatAddress(option.address || null);
            return (
              <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                  {highlightMatch(option.name, clientSearchInput)}
                </Typography>
                {address && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, textAlign: 'center' }}>
                    {highlightMatch(address, clientSearchInput)}
                  </Typography>
                )}
              </Box>
            );
          }}
        />
      </Box>
      )}

      {!onlyTasks && (
      <>
      {/* Numeration Schema & Number */}
      <Box className={styles.formRow}>
        <Box className={styles.field45}>
          <CustomAutocomplete
            apiConfig={{
              url: NUMERATION_SCHEMA_ENDPOINT,
            }}
            size="small"
            value={formData.numeration_schema_id}
            onChange={(_, newValue) => {
              setFormData((prev) => ({
                ...prev,
                numeration_schema_id: newValue,
                number: newValue ? prev.number : "",
              }));
              if (newValue) fetchNextNumber(newValue.id);
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Numeration schema")}
                error={Boolean(getFieldError("numeration_schema_id"))}
                helperText={getFieldError("numeration_schema_id") || ""}
                className={styles.inputBase}
              />
            )}
          />
        </Box>

        <Box className={styles.field45}>
          <TextField
            size="small"
            label={t("Number")}
            value={formData.number}
            onChange={(e) => setFormData((prev) => ({ ...prev, number: e.target.value }))}
            fullWidth
            disabled={isNumberDisabled || numberLoading}
            InputProps={{
              endAdornment: numberLoading ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("Loading...")}
                </Typography>
              ) : undefined,
            }}
            error={Boolean(getFieldError("number"))}
            helperText={getFieldError("number") || ""}
            className={styles.inputBase}
          />
        </Box>
      </Box>

      {/* Kind & Planned for */}
      <Box className={styles.formRow}>
        <Box className={styles.field45}>
          <TextField
            select
            size="small"
            label={t("Kind")}
            value={formData.kind}
            onChange={(e) => setFormData((prev) => ({ ...prev, kind: e.target.value }))}
            fullWidth
            error={Boolean(getFieldError("kind"))}
            helperText={getFieldError("kind") || ""}
            className={styles.inputBase}
          >
              {availableJobKindOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box className={styles.field45}>
          <CustomDatePicker
            label={t("Planned for")}
            value={formData.planned_for ? dayjs(formData.planned_for) : null}
            onChange={(value: Dayjs | null) =>
              setFormData((prev) => ({
                ...prev,
                planned_for: value ? value.format("YYYY-MM-DD") : null,
              }))
            }
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: Boolean(getFieldError("planned_for")),
                helperText: getFieldError("planned_for") || "",
                className: styles.inputBase,
              },
            }}
          />
        </Box>
      </Box>

      {/* Assigned To */}
      <Box className={styles.formRow}>
        <Box className={styles.field45}>
          <CustomAutocomplete
            multiple
            size="small"
            apiConfig={{
              url: USERS_ENDPOINT,
              queryParams: { is_active: "true", service_role: "true" },
            }}
            value={formData.assigned_to}
            onChange={(_, newValue) =>
              setFormData((prev) => ({
                ...prev,
                assigned_to: newValue,
              }))
            }
            getOptionLabel={(option) => option.full_name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Assigned to")}
                error={Boolean(getFieldError("assigned_to"))}
                helperText={getFieldError("assigned_to") || ""}
                className={styles.inputBase}
              />
            )}
          />
        </Box>
      </Box>

      {/* Description */}
      <TextField
        size="small"
        label={t("Description")}
        value={formData.description}
        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
        fullWidth
        multiline
        minRows={3}
        error={Boolean(getFieldError("description"))}
        helperText={getFieldError("description") || ""}
        className={styles.inputBase}
      />

      </>
      )}

      {/* Tasks Section */}
      <Box className={styles.servicesSection}>
        <Box className={styles.servicesHeader}>
          <Typography variant="h6" className={styles.headerTypography}>
            {t("Tasks")}
          </Typography>
          <Button variant="outlined" startIcon={<Add />} size="small" onClick={addTaskRow}>
            {t("Add task")}
          </Button>
        </Box>

        {formData.tasks.map((task, index) => {
          // Equipment already chosen in the other task rows - hidden from this
          // row's picker so the same equipment can't be added twice.
          const equipmentSelectedInOtherRows = formData.tasks
            .filter((_, i) => i !== index)
            .map((otherRow) => otherRow.equipment)
            .filter(
              (equipmentId): equipmentId is string | number =>
                equipmentId !== null && equipmentId !== undefined
            );

          return (
          <Box
            key={`task-${index}`}
            className={styles.serviceBox}
          >
            <Box className={styles.serviceRowHeader}>
              <Typography variant="subtitle1" className={styles.subtitleTypography}>
                {t("Task")} #{index + 1}
              </Typography>
              <IconButton size="small" color="error" onClick={() => removeTaskRow(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>

            {/* Equipment (first row) */}
            <Box className={styles.formRow}>
              <Box className={styles.field45}>
                <EquipmentAutocomplete
                  size="small"
                  value={
                    (autocompleteData.options["tasks.equipment"]?.find(
                      (opt) =>
                        opt?.id !== undefined && String(opt.id) === String(task.equipment)
                    ) || null) as any
                  }
                  onChange={(newValue) => {
                    if (newValue) {
                      setAutocompleteData((prev) => {
                        const current = prev.options["tasks.equipment"] || [];
                        const exists = current.some(
                          (opt) => opt?.id !== undefined && String(opt.id) === String(newValue.id)
                        );
                        if (exists) return prev;
                        return {
                          ...prev,
                          options: {
                            ...prev.options,
                            "tasks.equipment": [...current, newValue],
                          },
                        };
                      });
                      // Auto-fill client from equipment's active contract term, but only
                      // when no client is selected yet - do not override an existing choice
                      if (formData.client_id === null || formData.client_id === undefined) {
                        pendingClientLookupIdRef.current = newValue.id;
                        axios
                          .get(
                            `${CONTRACT_TERMS_ENDPOINT}?equipment=${newValue.id}&is_active=true`
                          )
                          .then(({ data }) => {
                            // Discard stale responses if a newer equipment was selected
                            if (pendingClientLookupIdRef.current !== newValue.id) return;
                            const results = Array.isArray(data?.results) ? data.results : data;
                            const firstTerm = Array.isArray(results) ? results[0] : null;
                            const clientId = firstTerm?.contract?.client_id ?? null;
                            if (clientId != null) {
                              // Update ref before state to prevent the client-change effect
                              // from clearing the just-selected equipment
                              previousClientIdRef.current = clientId;
                              axios
                                .get(`${CLIENTS_ENDPOINT}${clientId}/`)
                                .then(({ data: clientData }) => {
                                  if (clientData) {
                                    setSelectedClientOption(clientData);
                                    setFormData((prev) => ({ ...prev, client_id: clientId }));
                                  }
                                })
                                .catch(() => {});
                            }
                          })
                          .catch(() => {});
                      }
                    }
                    handleTaskFieldChange(index, "equipment", newValue ? newValue.id : null);
                  }}
                  clientId={formData.client_id !== null ? String(formData.client_id) : undefined}
                  filterUnattached={false}
                  onlyActiveContracts
                  excludeIds={equipmentSelectedInOtherRows}
                  hasNoAddress={formData.kind === "I"}
                />
              </Box>
            </Box>

            {/* Issues & Services (second row) */}
            <Box className={styles.formRow}>
              <Box className={styles.field45}>
                <CustomAutocomplete
                  multiple
                  apiConfig={{
                    url: JOB_ISSUES_ENDPOINT,
                  }}
                  size="small"
                  value={task.issues}
                  onChange={(_, newValue) =>
                    handleTaskFieldChange(
                      index,
                      "issues",
                      newValue
                    )
                  }
                  getOptionLabel={(option) => option.title}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Issues")}
                      error={Boolean(getServiceFieldError(index, "issues"))}
                      helperText={getServiceFieldError(index, "issues") || ""}
                      className={styles.inputBase}
                    />
                  )}
                />
              </Box>

              <Box className={styles.field45}>
                <CustomAutocomplete
                  multiple
                  apiConfig={{
                    url: JOB_SERVICES_ENDPOINT,
                  }}
                  size="small"
                  value={task.services}
                  onChange={(_, newValue) =>
                    handleTaskFieldChange(
                      index,
                      "services",
                      newValue
                    )
                  }
                  getOptionLabel={(option) => option.title}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Services")}
                      error={Boolean(getServiceFieldError(index, "services"))}
                      helperText={getServiceFieldError(index, "services") || ""}
                      className={styles.inputBase}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Task Description & Finished At */}
            <Box className={styles.formRow}>
              <TextField
                size="small"
                label={t("Task description")}
                value={task.description}
                onChange={(e) => handleTaskFieldChange(index, "description", e.target.value)}
                fullWidth
                multiline
                rows={2}
                error={Boolean(getServiceFieldError(index, "description"))}
                helperText={getServiceFieldError(index, "description") || ""}
                className={styles.inputBase}
              />
            </Box>

            {/* Task Attachments */}
            <Box className={styles.formRow}>
              <Box sx={{ flex: "1 1 100%" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("Attachments")}
                </Typography>
                <FileDropZoneField
                  onFileSelected={(file) => handleTaskAttachmentFile(index, file)}
                  uploading={uploadingTaskIndex === index}
                  disabled={uploadingTaskIndex !== null}
                />
                {task.attachments.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                    {task.attachments.map((chip) => {
                      // Read-only rather than hidden: the technician still
                      // needs to see what was reported, they just cannot
                      // remove it. The backend refuses either way.
                      const readOnly = isServiceRole && chip.origin === "R";
                      const chipElement = (
                        <Chip
                          key={chip.id}
                          label={chip.label}
                          size="small"
                          clickable={Boolean(chip.url)}
                          onClick={
                            chip.url
                              ? (e) => {
                                  e.preventDefault();
                                  window.open(chip.url, "_blank", "noopener,noreferrer");
                                }
                              : undefined
                          }
                          onDelete={
                            readOnly
                              ? undefined
                              : (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  void handleRemoveTaskAttachment(index, chip);
                                }
                          }
                          deleteIcon={readOnly ? undefined : <Close fontSize="small" />}
                        />
                      );

                      return readOnly ? (
                        <Tooltip
                          key={chip.id}
                          title={t("Attached when the job was reported.")}
                        >
                          <span>{chipElement}</span>
                        </Tooltip>
                      ) : (
                        chipElement
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          );
        })}
      </Box>

      {/* Submit Button */}
      <Box className={styles.submitButtonContainer}>
        <Button variant="contained" color="success" disabled={loading} onClick={handleSubmit}>
          {isEditMode ? t("Update") : t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
